import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import UserScreenWindow from "../Components/UserScreenWindow";
import {
  getDocDataSnap,
  addMessageToChat,
  markChatAsRead,
  storage,
  updateDocById,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import MessageCard from "./MessageCard";
import MessageSenderCard from "./MessageSenderCard";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { v4 } from "uuid";
import Modal from "../../Dashboard/Components/Modal";
import AddParticpantForm from "./AddParticpantForm";
import ProductDetailModal from "./ProductDetailModal";
import EmojiPicker from "emoji-picker-react";
import Linkify from "linkify-react";
import { find, registerCustomProtocol } from "linkifyjs";
import {
  FiSettings,
  FiSend,
  FiLoader,
  FiArrowLeft,
  FiShoppingBag,
  FiDollarSign,
  FiUser,
  FiMoreVertical,
  FiImage,
  FiSmile,
  FiPaperclip,
  FiX,
  FiExternalLink,
  FiTrash2,
} from "react-icons/fi";
import { FaMoneyBillWave } from "react-icons/fa";
import toast from "react-hot-toast";

function GroupChat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chat, setChat] = useState(null);
  const currentUser = useSelector((state) => state.userObject);
  const [formData, setFormData] = useState({});
  const messagesEndRef = useRef(null);
  const [addMembers, setAddMembers] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const [linkPreview, setLinkPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const messageInputTimeout = useRef(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  

  useEffect(() => {
    const fetchChat = async () => {
      // Try to fetch from directChats first
      let unsubscribe = getDocDataSnap("directChats", id, (data) => {
        if (data) {
          setChat({ ...data, type: "direct" });
          setMessages(data.messages || []);
          // Mark messages as read
          markChatAsRead(id, currentUser.uid);
          setLoading(false);
        } else {
          // If not found in directChats, try groupChats
          unsubscribe = getDocDataSnap("groupChats", id, (data) => {
            if (data) {
              setChat({ ...data, type: "group" });
              setMessages(data.messages || []);
              setLoading(false);
            } else {
              // Chat not found
              navigate("/chat");
            }
          });
        }
      });

      return () => unsubscribe && unsubscribe();
    };

    fetchChat();
  }, [id]);

  // useEffect(() => {
  //   scrollToBottom();
  // }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Add link detection and preview fetching
  useEffect(() => {
    const detectLinks = async () => {
      if (!formData.message) {
        setLinkPreview(null);
        return;
      }

      // Find links in the message
      const links = find(formData.message);
      if (links.length > 0) {
        const firstLink = links[0].href;

        // If we already have a preview for this link, don't fetch again
        if (linkPreview && linkPreview.url === firstLink) return;

        // Clear any existing timeout
        if (messageInputTimeout.current) {
          clearTimeout(messageInputTimeout.current);
        }

        // Add a delay to avoid fetching previews while user is still typing
        messageInputTimeout.current = setTimeout(() => {
          fetchLinkPreview(firstLink);
        }, 500);
      } else {
        setLinkPreview(null);
      }
    };

    detectLinks();

    return () => {
      if (messageInputTimeout.current) {
        clearTimeout(messageInputTimeout.current);
      }
    };
  }, [formData.message]);

  const fetchLinkPreview = async (url) => {
    try {
      setIsLoadingPreview(true);

      // Create a serverless proxy URL to avoid CORS issues
      const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;

      const response = await fetch(proxyUrl);
      const html = await response.text();

      // Parse HTML to extract OpenGraph metadata
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const preview = {
        url: url,
        title:
          getMetaContent(doc, "og:title") ||
          getMetaContent(doc, "twitter:title") ||
          doc.querySelector("title")?.textContent ||
          url,
        description:
          getMetaContent(doc, "og:description") ||
          getMetaContent(doc, "twitter:description") ||
          getMetaContent(doc, "description") ||
          "",
        image:
          getMetaContent(doc, "og:image") ||
          getMetaContent(doc, "twitter:image") ||
          "",
        siteName: getMetaContent(doc, "og:site_name") || new URL(url).hostname,
      };

      setLinkPreview(preview);
    } catch (error) {
      console.error("Error fetching link preview:", error);
      // If fetching fails, just store the URL
      setLinkPreview({
        url: url,
        title: url,
        siteName: new URL(url).hostname,
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const getMetaContent = (doc, name) => {
    const metaTag = doc.querySelector(
      `meta[property="${name}"], meta[name="${name}"]`
    );
    return metaTag ? metaTag.getAttribute("content") : null;
  };

  const clearLinkPreview = () => {
    setLinkPreview(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!formData.message?.trim() && !selectedFile) || isUploading) return;

    const docObject = {
      message: formData.message || "",
      messageStatus: "unread",
      displayName: currentUser.displayName,
      user: currentUser.email,
      dateTime: new Date(),
      id: v4(),
    };

    // If there's a link preview, add it to the message
    if (linkPreview) {
      docObject.linkPreview = linkPreview;
    }

    // If there's a file, upload it first
    if (selectedFile) {
      await handleFileUpload();
      return; // The message will be sent after upload completes
    }

    setFormData({});
    setLinkPreview(null);
    await addMessageToChat(id, chat.type === "group", docObject);
    scrollToBottom();
  };

  const handleEmojiClick = (emojiData) => {
    setFormData({
      ...formData,
      message: (formData.message || "") + emojiData.emoji,
    });
    // Optional: Close emoji picker after selection
    // setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size should be less than 10MB");
      return;
    }

    setSelectedFile({
      file,
      type: type === "image" ? "image" : "file",
      name: file.name,
      preview: type === "image" ? URL.createObjectURL(file) : null,
    });
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const fileName = `${Date.now()}_${selectedFile.name.replace(
        /[^a-zA-Z0-9.]/g,
        "_"
      )}`;
      const storageRef = ref(
        storage,
        `chatFiles/${id}/${selectedFile.type}/${fileName}`
      );

      const uploadTask = uploadBytesResumable(storageRef, selectedFile.file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(progress);
        },
        (error) => {
          toast.error("Upload failed: " + error.message);
          setIsUploading(false);
        },
        async () => {
          // Get the download URL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          // Create message object with file info
          const docObject = {
            message: formData.message || "",
            messageStatus: "unread",
            displayName: currentUser.displayName,
            user: currentUser.email,
            dateTime: new Date(),
            id: v4(),
            fileUrl: downloadURL,
            fileName: selectedFile.name,
            fileType: selectedFile.type,
          };

          // Send message with file
          await addMessageToChat(id, chat.type === "group", docObject);

          // Reset states
          setSelectedFile(null);
          setFormData({});
          setIsUploading(false);
          scrollToBottom();
        }
      );
    } catch (error) {
      toast.error("Error uploading file: " + error.message);
      setIsUploading(false);
    }
  };

  const clearSelectedFile = () => {
    if (selectedFile?.preview) {
      URL.revokeObjectURL(selectedFile.preview);
    }
    setSelectedFile(null);
  };

  const handleImageClick = () => {
    imageInputRef.current?.click();
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleAddParticipants = async () => {
    setAddMembers(!addMembers);
  };

  const handleBack = () => {
    navigate("/chat");
  };

  const handleViewProductDetails = () => {
    setShowProductDetails(true);
  };

  const getContextTitle = (chat) => {
    if (chat?.productContext) {
      // Check all possible title fields
      return (
        chat.productContext.productTitle ||
        chat.productContext.propertyTitle ||
        chat.productContext.unitNumber ||
        "Direct Message"
      );
    }
    return "Direct Message";
  };

  const getContextPrice = (chat) => {
    if (chat?.productContext) {
      return chat.productContext.price || chat.productContext.monthlyRent;
    }
    return null;
  };

  const getContextImage = (chat) => {
    if (chat?.productContext) {
      return (
        chat.productContext.productImage ||
        chat.productContext.propertyImage ||
        chat.productContext.parkingImage ||
        chat.productContext.images?.[0]
      );
    }
    return null;
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Small delay to ensure window scroll completes first
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  // Add click outside handler for emoji picker
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  const handleDeleteMessage = async (messageId) => {
    if (!messageId || !chat) return;

    try {
      // Find the message in the messages array
      const messageIndex = messages.findIndex((msg) => msg.id === messageId);
      if (messageIndex === -1) return;

      // Create a copy of the messages array
      const updatedMessages = [...messages];

      // Mark the message as deleted
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        isDeleted: true,
        message: "Message deleted",
        fileUrl: null,
        fileName: null,
        fileType: null,
        linkPreview: null,
      };

      // Update the chat document in Firestore
      const chatCollection =
        chat.type === "group" ? "groupChats" : "directChats";
      await updateDocById(chatCollection, id, { messages: updatedMessages });

      // Update local state
      setMessages(updatedMessages);
      setMessageToDelete(null);
      setDeleteConfirmVisible(false);

      toast.success("Message deleted");
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Failed to delete message");
    }
  };

  const handleOpenDeleteConfirm = (messageId) => {
    setMessageToDelete(messageId);
    setDeleteConfirmVisible(true);
  };

  const handleCloseDeleteConfirm = () => {
    setMessageToDelete(null);
    setDeleteConfirmVisible(false);
  };

  const handleLikeMessage = async (messageId) => {
    if (!messageId || !chat || !currentUser?.email) return;

    try {
      // Find the message in the messages array
      const messageIndex = messages.findIndex((msg) => msg.id === messageId);
      if (messageIndex === -1) return;

      // Create a copy of the messages array
      const updatedMessages = [...messages];

      // Initialize likes object if it doesn't exist
      if (!updatedMessages[messageIndex].likes) {
        updatedMessages[messageIndex].likes = {};
      }

      // Toggle like status
      if (updatedMessages[messageIndex].likes[currentUser.email]) {
        // Unlike if already liked
        delete updatedMessages[messageIndex].likes[currentUser.email];
      } else {
        // Like if not already liked
        updatedMessages[messageIndex].likes[currentUser.email] = true;
      }

      // Update the chat document in Firestore
      const chatCollection =
        chat.type === "group" ? "groupChats" : "directChats";
      await updateDocById(chatCollection, id, { messages: updatedMessages });

      // Update local state
      setMessages(updatedMessages);
    } catch (error) {
      console.error("Error liking message:", error);
      toast.error("Failed to like message");
    }
  };

  if (loading) {
    return (
      <UserScreenWindow label="Chat">
        <div className="flex items-center justify-center flex-1">
          <div className="flex flex-col items-center gap-4">
            <FiLoader className="w-8 h-8 animate-spin text-[#363062]" />
            <p className="text-gray-600">Loading messages...</p>
          </div>
        </div>
      </UserScreenWindow>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Chat Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white shadow-sm z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiArrowLeft className="w-5 h-5 text-[#363062]" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#363062]/10 flex items-center justify-center">
              {chat.type === "direct" ? (
                <FiUser className="w-5 h-5 text-[#363062]" />
              ) : (
                <div className="font-medium text-[#363062]">
                  {chat.name?.charAt(0)}
                </div>
              )}
            </div>
            <div>
              <h2 className="text-[15px] font-medium text-[#363062]">
                {chat.type === "direct" && chat.productContext
                  ? getContextTitle(chat)
                  : chat.type === "group"
                  ? chat.name
                  : "Direct Message"}
              </h2>
              <p className="text-xs text-gray-500">
                {chat.type === "group"
                  ? `${chat.members?.length} members`
                  : "Active now"}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {chat.type === "group" && (
            <button
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              onClick={handleAddParticipants}
            >
              <FiSettings className="w-5 h-5 text-[#363062]" />
            </button>
          )}
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <FiMoreVertical className="w-5 h-5 text-[#363062]" />
          </button>
        </div>
      </div>

      {/* Product Context Banner */}
      <AnimatePresence>
        {chat.type === "direct" && chat.productContext && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-gradient-to-r from-[#363062]/5 to-[#363062]/10 p-3 flex items-center justify-between border-b"
          >
            <div className="flex items-center gap-3">
              {getContextImage(chat) ? (
                <img
                  src={getContextImage(chat)}
                  alt={getContextTitle(chat)}
                  className="w-12 h-12 rounded-lg object-cover shadow-sm"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-[#363062]/10 flex items-center justify-center">
                  <FiShoppingBag className="w-5 h-5 text-[#363062]" />
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium text-[#363062] truncate max-w-[200px]">
                  {getContextTitle(chat)}
                </h3>
                <p className="text-[#8FCD00] text-sm font-semibold">
                  ₱{getContextPrice(chat)}
                </p>
              </div>
            </div>
            <button
              className="px-3 py-1.5 bg-[#8FCD00] text-white rounded-full text-sm hover:bg-[#8FCD00]/90 transition-colors flex items-center gap-1.5"
              onClick={handleViewProductDetails}
            >
              <FaMoneyBillWave className="w-4 h-4" />
              <span>View Details</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages Container */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <FiLoader className="w-6 h-6 animate-spin text-[#363062]" />
            <p className="text-sm text-gray-500">Loading messages...</p>
          </div>
        </div>
      ) : (
        <div
          className="flex-1 overflow-y-auto px-4 py-6 space-y-4"
          style={{
            backgroundImage:
              "radial-gradient(circle at center, #f8f9fa 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        >
          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex flex-col"
            >
              {message.user === currentUser.email ? (
                <MessageSenderCard
                  message={message}
                  canDelete={!message.isDeleted}
                  onDeleteRequest={() => handleOpenDeleteConfirm(message.id)}
                  onLikeMessage={handleLikeMessage}
                />
              ) : (
                <MessageCard
                  message={message}
                  onLikeMessage={handleLikeMessage}
                />
              )}
            </motion.div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Message Input Area */}
      <div className="border-t bg-white p-3">
        {selectedFile && (
          <div className="mb-3 p-2 bg-gray-100 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedFile.type === "image" && selectedFile.preview ? (
                <img
                  src={selectedFile.preview}
                  alt="Preview"
                  className="w-14 h-14 rounded object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                  <FiPaperclip className="w-5 h-5 text-gray-500" />
                </div>
              )}
              <div>
                <p className="text-sm font-medium truncate max-w-[200px]">
                  {selectedFile.name}
                </p>
                {isUploading && (
                  <div className="w-full bg-gray-200 h-1.5 rounded-full mt-1">
                    <div
                      className="bg-[#363062] h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={clearSelectedFile}
              className="p-1.5 hover:bg-gray-200 rounded-full"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Link Preview Card */}
        {linkPreview && !selectedFile && (
          <div className="mb-3 p-2 bg-gray-100 rounded-lg flex items-start justify-between">
            <div className="flex gap-2 w-full">
              {linkPreview.image ? (
                <img
                  src={linkPreview.image}
                  alt={linkPreview.title}
                  className="w-14 h-14 rounded object-cover bg-white flex-shrink-0"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-14 h-14 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                  <FiExternalLink className="w-5 h-5 text-gray-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#363062] font-medium">
                  {linkPreview.siteName}
                </p>
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {linkPreview.title}
                </p>
                {linkPreview.description && (
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {linkPreview.description}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={clearLinkPreview}
              className="p-1.5 hover:bg-gray-200 rounded-full flex-shrink-0 ml-2"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        )}

        {isLoadingPreview && (
          <div className="mb-3 p-2 bg-gray-100 rounded-lg flex items-center">
            <div className="w-5 h-5 border-2 border-[#363062] border-t-transparent rounded-full animate-spin mr-2"></div>
            <p className="text-xs text-gray-500">Loading link preview...</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="relative">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#363062]"
              onClick={handleFileClick}
            >
              <FiPaperclip className="w-5 h-5" />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={(e) => handleFileSelect(e, "file")}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
            />

            <div className="flex-1 relative">
              <input
                className="w-full px-4 py-2.5 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-[#363062]/20 text-[15px] pr-24"
                type="text"
                name="message"
                onChange={handleChange}
                value={formData.message || ""}
                placeholder="Type a message..."
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <button
                  type="button"
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors text-[#363062]"
                  onClick={handleImageClick}
                >
                  <FiImage className="w-5 h-5" />
                </button>
                <input
                  type="file"
                  ref={imageInputRef}
                  className="hidden"
                  onChange={(e) => handleFileSelect(e, "image")}
                  accept="image/*"
                />

                <button
                  type="button"
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors text-[#363062]"
                  onClick={toggleEmojiPicker}
                  ref={emojiButtonRef}
                >
                  <FiSmile className="w-5 h-5" />
                </button>
              </div>
            </div>

            <motion.button
              type="submit"
              className="p-3 bg-[#363062] text-white rounded-full hover:bg-[#363062]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 shadow-sm"
              disabled={
                (!formData.message?.trim() && !selectedFile) || isUploading
              }
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiSend className="w-5 h-5" />
            </motion.button>
          </div>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div
              className="absolute bottom-14 right-0 z-10"
              ref={emojiPickerRef}
            >
              <div className="p-1 bg-white rounded-lg shadow-lg border border-gray-200">
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  width={300}
                  height={350}
                />
              </div>
            </div>
          )}
        </form>
      </div>

      <AnimatePresence>
        {addMembers && (
          <Modal>
            <AddParticpantForm
              groupChat={chat}
              id={id}
              handleAddParticipants={handleAddParticipants}
            />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showProductDetails && chat?.productContext && (
          <Modal>
            <ProductDetailModal
              productContext={chat.productContext}
              onClose={() => setShowProductDetails(false)}
            />
          </Modal>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmVisible && (
          <Modal>
            <div className="bg-white rounded-lg shadow-lg p-5 w-full max-w-sm mx-auto">
              <h3 className="text-lg font-semibold text-[#363062] mb-3">
                Delete Message
              </h3>
              <p className="text-gray-600 mb-5">
                Are you sure you want to delete this message? This action cannot
                be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseDeleteConfirm}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteMessage(messageToDelete)}
                  className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1.5"
                >
                  <FiTrash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

export default GroupChat;
